import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Checkbox, FormControlLabel, FormHelperText, TextField, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import allCountries from 'country-region-data'
import { useSnackbar } from 'notistack'
import { useDebouncedCallback } from 'use-debounce'
import { getDetails } from 'use-places-autocomplete'

import {
    useGetContactQuery,
    useGetCustomerQuery,
    useUpdateContactMutation,
    useUpdateCustomerMutation,
} from '@/app/services/customer'
import { useUpdateUserMutation } from '@/app/services/user'
import { selectCurrentUser } from '@/app/slices/appSlice'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { AlertDialog, TbxTooltip } from '@/common/components'
import SettingsToolbar from '@/common/components/AppBar/SettingsToolbar'
import FormInputAddress from '@/common/components/FormComponents/FormInputAddress'
import FormInputCheckbox from '@/common/components/FormComponents/FormInputCheckbox'
import FormInputText from '@/common/components/FormComponents/FormInputText'
import LanguageSelect from '@/common/components/LanguageSelect/LanguageSelect'
import { getAddressDetailsFromAddressComponents, validateAddress } from '@/common/helpers/addressUtilities'
import { fractionToPercentage, Paths } from '@/common/utils'

const classes = {
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flexStart',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.grey[300]}`,
        paddingInline: 4,
        paddingBlock: 3,
        gap: 4,
    },

    sections: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignSelf: 'stretch',
    },
}

const AccountSettings = () => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { isAuthenticated, isLoading } = useKindeAuth()

    if (!isLoading && !isAuthenticated) {
        return <Navigate to={Paths.HOME_PATHNAME} />
    }

    const currentUser = useSelector(selectCurrentUser)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const organisationId = useSelector(selectOrganisationId)

    const { data: currentCustomer, isLoading: isLoadingCustomer } = useGetCustomerQuery({
        organisationId,
        customerId: currentCustomerId,
    })
    const { data: currentContact } = useGetContactQuery({
        organisationId,
        customerId: currentCustomerId,
        contactId: currentContactId,
    })

    const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation()
    const [updateCustomer, { isLoading: isUpdatingCustomer }] = useUpdateCustomerMutation()
    const [updateContact, { isLoading: isUpdatingContact }] = useUpdateContactMutation()

    const [showLanguageChangeAlert, setShowLanguageChangeAlert] = useState(false)
    const [enterBillingAddressManually, setEnterBillingAddressManually] = useState(
        currentCustomer?.billingAddress?.line1 ? true : false
    )
    const [enterDeliveryAddressManually, setEnterDeliveryAddressManually] = useState(
        currentCustomer?.deliveryAddress?.line1 ? true : false
    )
    const [selectedBillingGoogleAddress, setSelectedBillingGoogleAddress] = useState(null)
    const [selectedDeliveryGoogleAddress, setSelectedDeliveryGoogleAddress] = useState(null)

    const fullname = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`

    const {
        clearErrors,
        control,
        formState: { errors, isDirty, isSubmitting },
        getValues,
        handleSubmit,
        reset,
        resetField,
        setError,
        setValue,
        watch,
    } = useForm({
        mode: 'all',
        defaultValues: {
            firstName: currentUser?.firstName || '',
            lastName: currentUser?.lastName || '',
            companyName: currentCustomer?.companyName === fullname ? '' : currentCustomer?.companyName,
            phone: currentContact?.phone || '',
            email: currentContact?.email || '',
            displayLanguage: currentUser?.language,
            billingAddress: {
                googleAddress: null,
                line1: currentCustomer?.billingAddress?.line1 || '',
                line2: currentCustomer?.billingAddress?.line2 || '',
                city: currentCustomer?.billingAddress?.city || '',
                state: currentCustomer?.billingAddress?.state || '',
                country: currentCustomer?.billingAddress?.country || '',
                postcode: currentCustomer?.billingAddress?.postcode || '',
            },
            billingCountryCode: currentCustomer?.billingCountryCode || '',
            deliveryAddress: {
                googleAddress: null,
                line1: currentCustomer?.deliveryAddress?.line1 || '',
                line2: currentCustomer?.deliveryAddress?.line2 || '',
                city: currentCustomer?.deliveryAddress?.city || '',
                state: currentCustomer?.deliveryAddress?.state || '',
                country: currentCustomer?.deliveryAddress?.country || '',
                postcode: currentCustomer?.deliveryAddress?.postcode || '',
            },
            deliveryCountryCode: currentCustomer?.deliveryCountryCode || '',
            useOneAddress: currentCustomer?.useOneAddress ?? false,
        },
    })

    const billingAddressFields = watch([
        'billingAddress.line1',
        'billingAddress.city',
        'billingAddress.state',
        'billingAddress.country',
    ])
    const deliveryAddressFields = watch([
        'deliveryAddress.line1',
        'deliveryAddress.city',
        'deliveryAddress.state',
        'deliveryAddress.country',
    ])

    const billingGoogleAddressField = watch('billingAddress.googleAddress')
    const deliveryGoogleAddressField = watch('deliveryAddress.googleAddress')
    const useOneAddress = watch('useOneAddress')

    const hasCompletedBillingAddress = useMemo(() => {
        return billingAddressFields.every((value) => Boolean(value))
    }, [billingAddressFields])

    const hasCompletedDeliveryAddress = useMemo(() => {
        return deliveryAddressFields.every((value) => Boolean(value))
    }, [deliveryAddressFields])

    const countryName = useCallback(
        (countryCode) => {
            const country = allCountries.find((country) => country.countryShortCode === countryCode?.toUpperCase())
            return country?.countryName ?? null
        },
        [allCountries]
    )

    const stateName = useCallback(
        (countryCode, stateCode) => {
            const country = allCountries.find((country) => country.countryShortCode === countryCode?.toUpperCase())
            const state = country?.regions.find((region) => region.shortCode === stateCode)
            return state?.name ?? null
        },
        [allCountries]
    )

    const label = (taxRate) => {
        const { country, displayName, percentage, state } = taxRate
        const stateLabel = state ? `${stateName(country, state)} - ` : ''
        const countryLabel = country ? `${countryName(country)} - ` : ''
        const percentageLabel = fractionToPercentage(percentage)
        return `${displayName} - ${stateLabel}${countryLabel} (${percentageLabel}%)`
    }

    const setInvalidAddressError = (addressType) => {
        const errorField = addressType === 'billingAddress' ? 'invalidBillingAddress' : 'invalidDeliveryAddress'
        const errorMessage = t(
            'We could not find the specified address. Please ensure that the address is correct and try again.'
        )

        setError(errorField, {
            type: 'invalidAddress',
            message: errorMessage,
        })
    }

    const clearInvalidAddressError = (addressType) => {
        const errorField = addressType === 'billingAddress' ? 'invalidBillingAddress' : 'invalidDeliveryAddress'
        clearErrors(errorField)
    }

    const setAddressDetails = (addressType, addressDetails, allFields = true) => {
        const { addressLine1, city, country, countryCode, postcode, state, subpremise } =
            getAddressDetailsFromAddressComponents(addressDetails)

        if (allFields) {
            setValue(`${addressType}.line1`, addressLine1)
            setValue(`${addressType}.line2`, subpremise)
            setValue(`${addressType}.city`, city)
            setValue(`${addressType}.state`, state)
            setValue(`${addressType}.country`, country)
            setValue(`${addressType}.postcode`, postcode)
        }

        if (addressType === 'billingAddress') {
            setValue('billingCountryCode', countryCode)
            setSelectedBillingGoogleAddress(addressDetails)

            if (useOneAddress) {
                setValue('deliveryCountryCode', countryCode)
                setSelectedDeliveryGoogleAddress(addressDetails)
            }
        }

        if (addressType === 'deliveryAddress') {
            setValue('deliveryCountryCode', countryCode)
            setSelectedDeliveryGoogleAddress(addressDetails)
        }
    }

    const clearAddressDetails = (addressType) => {
        resetField(`${addressType}.line1`, { defaultValue: '' })
        resetField(`${addressType}.line2`, { defaultValue: '' })
        resetField(`${addressType}.city`, { defaultValue: '' })
        resetField(`${addressType}.state`, { defaultValue: '' })
        resetField(`${addressType}.country`, { defaultValue: '' })
        resetField(`${addressType}.postcode`, { defaultValue: '' })
        if (addressType === 'billingAddress') {
            resetField('useOneAddress', { defaultValue: false })
            resetField('billingCountryCode', { defaultValue: '' })
            setSelectedBillingGoogleAddress(null)
        }

        if (addressType === 'deliveryAddress') {
            resetField('deliveryCountryCode', { defaultValue: '' })
            setSelectedDeliveryGoogleAddress(null)
        }
    }

    const fetchGoogleAddressFromCustomer = useCallback(
        async (addressType) => {
            const addressComponents = ['line1', 'city', 'state', 'country', 'postcode']

            const getAddressComponent = (component) => currentCustomer?.[`${addressType}`]?.[component] || ''

            const addressValues = addressComponents.map(getAddressComponent)
            const fullAddress = addressValues.filter(Boolean).join(', ')

            if (!fullAddress) return

            const { formattedAddress, placeId } = await validateAddress({ address: fullAddress })

            addressComponents.forEach((component, index) => {
                setValue(`${addressType}.${component}`, addressValues[index], {
                    shouldDirty: false,
                    shouldValidate: false,
                    shouldTouch: false,
                })
            })

            const detailedAddress = await getDetails({ placeId })

            if (addressType === 'billingAddress') {
                setSelectedBillingGoogleAddress(detailedAddress)
                resetField('billingAddress.googleAddress', {
                    defaultValue: { description: formattedAddress, place_id: placeId },
                })
            } else if (addressType === 'deliveryAddress') {
                setSelectedDeliveryGoogleAddress(detailedAddress)
                resetField('deliveryAddress.googleAddress', {
                    defaultValue: { description: formattedAddress, place_id: placeId },
                })
            }
        },
        [currentCustomer]
    )

    const validateManualAddress = useCallback(
        async (addressType) => {
            const addressComponents = ['line1', 'city', 'state', 'country', 'postcode']
            const getAddressComponent = (component) => getValues(`${addressType}.${component}`) || ''
            const addressValues = addressComponents.map(getAddressComponent)
            const fullAddress = addressValues.filter(Boolean).join(', ')

            if (!fullAddress) return

            const { formattedAddress, isValid, placeId } = await validateAddress({ address: fullAddress })

            if (!isValid) {
                setInvalidAddressError(addressType)
                resetField(`${addressType}.googleAddress`, { defaultValue: null })
                return
            }

            const addressDetails = await getDetails({ placeId })
            clearInvalidAddressError(addressType)
            setAddressDetails(addressType, addressDetails, false)

            if (addressType === 'billingAddress') {
                setSelectedBillingGoogleAddress(addressDetails)
                setValue('billingAddress.googleAddress', { description: formattedAddress, place_id: placeId })

                if (useOneAddress) {
                    setSelectedDeliveryGoogleAddress(addressDetails)
                    setValue('deliveryAddress.googleAddress', { description: formattedAddress, place_id: placeId })
                }
            } else if (addressType === 'deliveryAddress') {
                setSelectedDeliveryGoogleAddress(addressDetails)
                setValue('deliveryAddress.googleAddress', { description: formattedAddress, place_id: placeId })
            }
        },
        [
            getValues,
            setValue,
            clearInvalidAddressError,
            setInvalidAddressError,
            selectedBillingGoogleAddress,
            selectedDeliveryGoogleAddress,
        ]
    )

    const debounceValidateManualAddress = useDebouncedCallback(validateManualAddress, 1500)

    const handleAddressFieldChange = (_e, addressType) => {
        debounceValidateManualAddress.cancel()
        if (addressType === 'billingAddress' && hasCompletedBillingAddress) {
            debounceValidateManualAddress(addressType)
        }

        if (addressType === 'deliveryAddress' && hasCompletedDeliveryAddress) {
            debounceValidateManualAddress(addressType)
        }
    }

    const handlePageReload = () => {
        setShowLanguageChangeAlert(false)
        window.location.reload()
    }

    const getAddress = (address, type) => ({
        addressType: type,
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: address.state,
        country: address.country,
        postcode: address.postcode,
    })

    const updateCurrentUser = (data) => {
        const user = {
            userId: currentUser.userId,
            firstName: data.firstName,
            lastName: data.lastName,
            language: data.displayLanguage != 'none' ? data.displayLanguage : '',
        }

        return updateUser({ organisationId, userId: currentUser.userId, user })
    }

    const handleUpdateCustomer = (data) => {
        const { billingAddress, billingCountryCode, companyName, deliveryAddress, deliveryCountryCode, useOneAddress } =
            data

        const company = companyName ? companyName : name

        const resolvedDeliveryAddress = useOneAddress
            ? getAddress(billingAddress, 'Delivery')
            : getAddress(deliveryAddress, 'Delivery')

        return updateCustomer({
            organisationId,
            customerId: currentCustomer.customerId,
            customer: {
                customerId: currentCustomer.customerId,
                companyName: company,
                useOneAddress: useOneAddress,
                billingAddress: getAddress(billingAddress, 'Billing'),
                deliveryAddress: resolvedDeliveryAddress,
                deliveryCountryCode,
                billingCountryCode,
            },
        })
    }

    const handleUpdateContact = (data) => {
        const { firstName, lastName, phone } = data

        const name = `${firstName} ${lastName}`

        return updateContact({
            organisationId,
            customerId: currentCustomer.customerId,
            contactId: currentContact.contactId,
            contact: {
                contactId: currentContact.contactId,
                customerId: currentCustomer.customerId,
                email: currentContact.email,
                name,
                phone,
            },
        })
    }

    const fetchCustomerAddresses = async () => {
        await fetchGoogleAddressFromCustomer('billingAddress')

        await fetchGoogleAddressFromCustomer('deliveryAddress')
    }

    const onSubmit = async (data) => {
        try {
            await updateCurrentUser(data).unwrap()
            await handleUpdateCustomer(data).unwrap()
            await handleUpdateContact(data).unwrap()

            if (data.displayLanguage != currentUser.language) {
                setShowLanguageChangeAlert(true)
            }

            enqueueSnackbar(t('Account settings updated successfully'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(t('Failed to update account settings'), { variant: 'error' })
        }
    }

    useEffect(() => {
        if (!isLoadingCustomer && !isUpdatingCustomer && currentCustomer) {
            fetchCustomerAddresses()
        }
    }, [isLoadingCustomer, currentCustomer, isUpdatingCustomer])

    useEffect(() => {
        reset({
            firstName: currentUser?.firstName || '',
            lastName: currentUser?.lastName || '',
            companyName: currentCustomer?.companyName === fullname ? '' : currentCustomer?.companyName,
            phone: currentContact?.phone || '',
            email: currentContact?.email || '',
            displayLanguage: currentUser?.language,
            billingAddress: {
                line1: currentCustomer?.billingAddress?.line1 || '',
                line2: currentCustomer?.billingAddress?.line2 || '',
                city: currentCustomer?.billingAddress?.city || '',
                state: currentCustomer?.billingAddress?.state || '',
                country: currentCustomer?.billingAddress?.country || '',
                postcode: currentCustomer?.billingAddress?.postcode || '',
            },
            billingCountryCode: currentCustomer?.billingCountryCode || '',
            deliveryAddress: {
                line1: currentCustomer?.deliveryAddress?.line1 || '',
                line2: currentCustomer?.deliveryAddress?.line2 || '',
                city: currentCustomer?.deliveryAddress?.city || '',
                state: currentCustomer?.deliveryAddress?.state || '',
                country: currentCustomer?.deliveryAddress?.country || '',
                postcode: currentCustomer?.deliveryAddress?.postcode || '',
            },
            deliveryCountryCode: currentCustomer?.deliveryCountryCode || '',
            useOneAddress: currentCustomer?.useOneAddress ?? false,
        })
    }, [currentCustomer, currentContact, currentUser])

    return (
        <>
            <SettingsToolbar
                enableCancelButton={isDirty ? !isSubmitting : null}
                enableSaveButton={isDirty ? !isSubmitting : null}
                handleCancelClick={() => reset()}
                handleSaveClick={handleSubmit(onSubmit)}
                isLoading={isUpdatingUser || isUpdatingCustomer || isUpdatingContact}
            />
            <Grid
                component="section"
                justifyContent="center"
                size={{
                    xs: 12,
                }}
                container
            >
                <Grid
                    size={{ xs: 6 }}
                    sx={classes.mainContent}
                >
                    <Typography variant="h6">{t('MY ACCOUNT')}</Typography>

                    <Box sx={classes.sections}>
                        <Box id="profile">
                            <Typography variant="h6">{t('Profile')}</Typography>
                        </Box>

                        <Grid
                            spacing={3}
                            sx={classes.fieldContainer}
                            container
                        >
                            <Grid size={6}>
                                <Controller
                                    control={control}
                                    name="firstName"
                                    render={({ field: { onBlur, onChange, ref, value }, fieldState }) => (
                                        <TextField
                                            error={Boolean(fieldState.error)}
                                            helperText={t(fieldState.error?.message)}
                                            inputRef={ref}
                                            label={t('First name')}
                                            value={value}
                                            fullWidth
                                            required
                                            onBlur={onBlur}
                                            onChange={onChange}
                                        />
                                    )}
                                    rules={{ required: t('Required') }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Controller
                                    control={control}
                                    name="lastName"
                                    render={({ field: { onBlur, onChange, ref, value }, fieldState }) => (
                                        <TextField
                                            error={Boolean(fieldState.error)}
                                            helperText={t(fieldState.error?.message)}
                                            inputRef={ref}
                                            label={t('Last name')}
                                            value={value}
                                            fullWidth
                                            required
                                            onBlur={onBlur}
                                            onChange={onChange}
                                        />
                                    )}
                                    rules={{ required: t('Required') }}
                                />
                            </Grid>

                            <Grid size={6}>
                                <TextField
                                    label={t('Email')}
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                        },
                                    }}
                                    value={currentContact?.email}
                                    aria-readonly
                                    disabled
                                    fullWidth
                                />
                            </Grid>

                            <Grid size={6}>
                                <Controller
                                    control={control}
                                    name="phone"
                                    render={({ field: { onBlur, onChange, ref, value }, fieldState }) => (
                                        <TextField
                                            error={Boolean(fieldState.error)}
                                            helperText={t(fieldState.error?.message)}
                                            inputRef={ref}
                                            label={t('Phone number')}
                                            value={value}
                                            fullWidth
                                            onBlur={onBlur}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid size={6}>
                                <Controller
                                    control={control}
                                    name="companyName"
                                    render={({ field: { onBlur, onChange, ref, value }, fieldState }) => (
                                        <TextField
                                            error={Boolean(fieldState.error)}
                                            helperText={t(fieldState.error?.message)}
                                            inputRef={ref}
                                            label={t('Company/Business')}
                                            value={value}
                                            fullWidth
                                            onBlur={onBlur}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </Grid>

                            {currentCustomer?.taxRate ? (
                                <Grid size={6}>
                                    <TextField
                                        label={t('Tax Rate')}
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                            },
                                        }}
                                        value={label(currentCustomer?.taxRate)}
                                        aria-readonly
                                        disabled
                                        fullWidth
                                    />
                                </Grid>
                            ) : null}

                            <Grid size={6}>
                                <Controller
                                    control={control}
                                    name="displayLanguage"
                                    render={({ field: { onChange, value } }) => (
                                        <LanguageSelect
                                            disabled={isSubmitting}
                                            nullValueOptionLabelText={t('Inherit default language')}
                                            showNullValueOption={true}
                                            value={value || 'none'}
                                            fullWidth
                                            onBlur={(value) => onChange(value || 'none')}
                                            onChange={(value) => onChange(value || 'none')}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid size={12}>
                                <Typography
                                    color="text.secondary"
                                    variant="body2"
                                >
                                    {t('To reset your password, use the Reset Password option on the login page.')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={classes.sections}>
                        <Box id="billingAddress">
                            <Typography variant="h6">{t('Billing Address')}</Typography>
                        </Box>

                        <Grid
                            spacing={3}
                            sx={classes.fieldContainer}
                            container
                        >
                            <Grid size={12}>
                                <FormInputAddress
                                    control={control}
                                    label={t('Search address')}
                                    name="billingAddress.googleAddress"
                                    readOnly={enterBillingAddressManually}
                                    rules={{ required: 'This field is required' }}
                                    shouldUnregister={true}
                                    onAddressChanged={setAddressDetails}
                                    onAddressClear={clearAddressDetails}
                                />
                            </Grid>

                            <Grid size={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={enterBillingAddressManually}
                                            color="secondary"
                                            size="small"
                                            onChange={() =>
                                                setEnterBillingAddressManually(!enterBillingAddressManually)
                                            }
                                        />
                                    }
                                    label={t('Enter billing address manually')}
                                />
                            </Grid>

                            {enterBillingAddressManually ? (
                                <>
                                    <Grid size={6}>
                                        <FormInputText
                                            control={control}
                                            label={t('Address')}
                                            name="billingAddress.line1"
                                            rules={{ required: 'This field is required' }}
                                            onHandleChange={(e) => handleAddressFieldChange(e, 'billingAddress')}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormInputText
                                            control={control}
                                            label={t('Unit/Suite/Floor/Office (optional)')}
                                            name="billingAddress.line2"
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormInputText
                                            control={control}
                                            label={t('Zip/Postal code')}
                                            name="billingAddress.postcode"
                                            onHandleChange={(e) => handleAddressFieldChange(e, 'billingAddress')}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormInputText
                                            control={control}
                                            label={t('City/County/Town/Suburb')}
                                            name="billingAddress.city"
                                            rules={{ required: 'This field is required' }}
                                            onHandleChange={(e) => handleAddressFieldChange(e, 'billingAddress')}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormInputText
                                            control={control}
                                            label={t('State/Province/Region')}
                                            name="billingAddress.state"
                                            rules={{ required: 'This field is required' }}
                                            onHandleChange={(e) => handleAddressFieldChange(e, 'billingAddress')}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormInputText
                                            control={control}
                                            label={t('Country')}
                                            name="billingAddress.country"
                                            rules={{ required: 'This field is required' }}
                                            onHandleChange={(e) => handleAddressFieldChange(e, 'billingAddress')}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <FormHelperText error={Boolean(errors?.invalidBillingAddress)}>
                                            {t(errors?.invalidBillingAddress?.message)}
                                        </FormHelperText>
                                    </Grid>
                                </>
                            ) : null}
                        </Grid>
                    </Box>

                    <Box sx={classes.sections}>
                        <Box id="profile">
                            <Typography variant="h6">{t('Delivery Address')}</Typography>
                        </Box>

                        <Grid
                            spacing={3}
                            sx={classes.fieldContainer}
                            container
                        >
                            <Grid size={12}>
                                <TbxTooltip
                                    title={
                                        hasCompletedBillingAddress || Boolean(billingGoogleAddressField?.place_id)
                                            ? ''
                                            : t('Please fulfill billing address details before selecting this option.')
                                    }
                                    arrow
                                >
                                    <span>
                                        <FormInputCheckbox
                                            color="secondary"
                                            control={control}
                                            disabled={
                                                !hasCompletedBillingAddress
                                                    ? !billingGoogleAddressField?.place_id
                                                    : null
                                            }
                                            label={t('My delivery address is the same as my billing address')}
                                            name="useOneAddress"
                                            size="small"
                                        />
                                    </span>
                                </TbxTooltip>
                            </Grid>

                            {!useOneAddress ? (
                                <>
                                    <Grid size={12}>
                                        <FormInputAddress
                                            control={control}
                                            hideError={enterDeliveryAddressManually}
                                            label={t('Search address')}
                                            name="deliveryAddress.googleAddress"
                                            readOnly={enterDeliveryAddressManually}
                                            rules={{ required: t('This field is required') }}
                                            shouldUnregister={true}
                                            onAddressChanged={setAddressDetails}
                                            onAddressClear={clearAddressDetails}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={enterDeliveryAddressManually}
                                                    color="secondary"
                                                    size="small"
                                                    onChange={() =>
                                                        setEnterDeliveryAddressManually(!enterDeliveryAddressManually)
                                                    }
                                                />
                                            }
                                            label={t('Enter delivery address manually')}
                                        />
                                    </Grid>

                                    {enterDeliveryAddressManually ? (
                                        <>
                                            <Grid size={6}>
                                                <FormInputText
                                                    control={control}
                                                    disabled={!enterDeliveryAddressManually}
                                                    label={t('Address')}
                                                    name="deliveryAddress.line1"
                                                    rules={{ required: 'This field is required' }}
                                                    onHandleChange={(e) =>
                                                        handleAddressFieldChange(e, 'deliveryAddress')
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormInputText
                                                    control={control}
                                                    label={t('Unit/Suite/Floor/Office (optional)')}
                                                    name="deliveryAddress.line2"
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormInputText
                                                    control={control}
                                                    disabled={!enterDeliveryAddressManually}
                                                    label={t('Post Code')}
                                                    name="deliveryAddress.postcode"
                                                    onHandleChange={(e) =>
                                                        handleAddressFieldChange(e, 'deliveryAddress')
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormInputText
                                                    control={control}
                                                    disabled={!enterDeliveryAddressManually}
                                                    label={t('City')}
                                                    name="deliveryAddress.city"
                                                    rules={{ required: 'This field is required' }}
                                                    onHandleChange={(e) =>
                                                        handleAddressFieldChange(e, 'deliveryAddress')
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormInputText
                                                    control={control}
                                                    disabled={!enterDeliveryAddressManually}
                                                    label={t('State')}
                                                    name="deliveryAddress.state"
                                                    rules={{ required: 'This field is required' }}
                                                    onHandleChange={(e) =>
                                                        handleAddressFieldChange(e, 'deliveryAddress')
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <FormInputText
                                                    control={control}
                                                    disabled={!enterDeliveryAddressManually}
                                                    label={t('Country')}
                                                    name="deliveryAddress.country"
                                                    rules={{ required: 'This field is required' }}
                                                    onHandleChange={(e) =>
                                                        handleAddressFieldChange(e, 'deliveryAddress')
                                                    }
                                                />
                                            </Grid>

                                            <Grid size={12}>
                                                <FormHelperText error={Boolean(errors?.invalidDeliveryAddress)}>
                                                    {t(errors?.invalidDeliveryAddress?.message) || ''}
                                                </FormHelperText>
                                            </Grid>
                                        </>
                                    ) : null}
                                </>
                            ) : null}
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
            <AlertDialog
                content={t('For language change to take effect, the page needs to be reloaded.')}
                hideCancelButton={true}
                okButtonText={t('Reload page')}
                open={showLanguageChangeAlert}
                title={t('Language change')}
                onOkClose={handlePageReload}
            />
        </>
    )
}

export default AccountSettings
